import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { UserFilter, UserStatus, UserType } from '@/gql/graphql';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Select } from '@/components/ui/Select';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { useSession } from '@/features/session/useSession';
import { UserRoleOptionsDoc } from '@/features/users/userDocuments';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  optionsOf,
  optionToYesNo,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
  yesNoToOption,
} from '@/hooks/useUrlState';
import {
  USER_COLUMN_SORT,
  USER_STATUS_LABELS,
  USER_TYPE_LABELS,
  USERS_ADVANCED_KEYS,
  USERS_DEFAULT_SORT,
  usersUrlSchema,
  type UserTypeFilter,
} from '@/features/users/usersUrlState';
import {
  userDisplayName,
  userStatusLabel,
  userStatusTone,
  userTypeLabel,
} from '@/lib/labels';

const UsersDoc = graphql(/* GraphQL */ `
  query Users(
    $page: Int
    $pageSize: Int
    $filter: UserFilter
    $sortField: String
    $sortDir: String
  ) {
    users(
      page: $page
      pageSize: $pageSize
      filter: $filter
      sortField: $sortField
      sortDir: $sortDir
    ) {
      nodes {
        id
        firstName
        lastName
        email
        type
        status
        marketingOptIn
        passkeyCount
        lastLoginAt
        defaultScope {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

interface UserRow {
  id: string;
  firstName: string;
  lastName?: string | null;
  email: string;
  type?: UserType | null;
  status: UserStatus;
  marketingOptIn: boolean;
  passkeyCount: number;
  lastLoginAt?: string | null;
  defaultScope?: { id: string; name: string } | null;
}

export function UsersListPage() {
  const { me } = useSession();
  const navigate = useNavigate();
  const { state: url, patch, setFilters } = useUrlState(usersUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => USERS_ADVANCED_KEYS.some((key) => !isDefault(usersUrlSchema, url, key)));
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, USERS_DEFAULT_SORT));
  };

  const sortField = USER_COLUMN_SORT[url.sort];
  const sortDir = url.dir === 'desc' ? 'DESC' : 'ASC';

  const filter = useMemo<UserFilter>(
    () => ({
      search: url.search || null,
      defaultScopeId: url.scope || null,
      status: url.status ? [url.status] : null,
      type: url.type && url.type !== 'CUSTOMER' ? [url.type] : null,
      customerOnly: url.type === 'CUSTOMER' || null,
      marketingOptIn: url.marketing,
      roleId: url.role || null,
      hasGoldenTicket: url.golden,
    }),
    [url],
  );

  const { data, previousData, loading, error } = useQuery(UsersDoc, {
    variables: { page: url.page, pageSize: url.size, filter, sortField, sortDir },
    errorPolicy: 'all',
  });
  const { data: roleOptionsData } = useQuery(UserRoleOptionsDoc);

  const viewData = data ?? previousData;
  const rows: UserRow[] = useMemo(
    () => viewData?.users.nodes ?? [],
    [viewData],
  );
  const pageInfo = viewData?.users.pageInfo;

  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/users/${row.original.id}`}
            state={{ from: listHref }}
            className="hover:text-brand font-medium text-gray-900 dark:text-white"
          >
            {userDisplayName(row.original)}
            <span
              id="admin.users.email"
              className="block text-xs text-gray-500"
            >
              {row.original.email}
            </span>
          </Link>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => userTypeLabel(row.original.type),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={userStatusTone(row.original.status)}>
            {userStatusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        id: 'marketing',
        header: 'Marketing',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={row.original.marketingOptIn ? 'success' : 'neutral'}>
            {row.original.marketingOptIn ? 'Opted in' : 'Opted out'}
          </Badge>
        ),
      },
      {
        id: 'passkeys',
        header: 'Passkeys',
        cell: ({ row }) => (
          <Badge tone={row.original.passkeyCount > 0 ? 'success' : 'warning'}>
            {row.original.passkeyCount}
          </Badge>
        ),
      },
      {
        id: 'lastLogin',
        header: 'Last login',
        cell: ({ row }) =>
          row.original.lastLoginAt ? (
            new Date(row.original.lastLoginAt).toLocaleString()
          ) : (
            <span className="text-gray-400">Never</span>
          ),
      },
      {
        id: 'scope',
        header: 'Default scope',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.defaultScope ? row.original.defaultScope.name : '—',
      },
    ],
    [listHref],
  );

  const scopeOptions = useMemo(
    () => [
      { value: '', label: 'Any default scope' },
      ...(me?.allowedScopes.map((s) => ({ value: s.id, label: s.name })) ?? []),
    ],
    [me?.allowedScopes],
  );

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'Any role' },
      ...(roleOptionsData?.userRoleOptions.map((r) => ({
        value: String(r.id),
        label: r.name,
      })) ?? []),
    ],
    [roleOptionsData?.userRoleOptions],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Users
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Accounts across customers and staff. Status and marketing opt-in are
            read-only here except block / unblock on the detail page.
          </p>
        </div>
        <Button
          onClick={() => navigate('/users/new', { state: { from: listHref } })}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          New user
        </Button>
      </div>

      {error ? (
        <QueryErrorNotice error={error} title="Could not load users" />
      ) : null}

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.users.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search name or email…',
        }}
        advanced={
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Status
              </span>
              <Select
                id="admin.users.status"
                value={url.status}
                onChange={(event) => setFilters({ status: event.target.value as UserStatus | '' })}
                options={optionsOf(USER_STATUS_LABELS, 'Any status')}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Type
              </span>
              <Select
                id="admin.users.type"
                value={url.type}
                onChange={(event) => setFilters({ type: event.target.value as UserTypeFilter | '' })}
                options={optionsOf(USER_TYPE_LABELS, 'Any type')}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Marketing opt-in
              </span>
              <Select
                id="admin.users.marketing"
                value={yesNoToOption(url.marketing)}
                onChange={(event) => setFilters({ marketing: optionToYesNo(event.target.value) })}
                options={[
                  { value: '', label: 'Any' },
                  { value: 'yes', label: 'Opted in' },
                  { value: 'no', label: 'Opted out' },
                ]}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Role
              </span>
              <Select
                id="admin.users.role"
                value={url.role}
                onChange={(event) => setFilters({ role: event.target.value })}
                options={roleOptions}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Golden ticket
              </span>
              <Select
                id="admin.users.goldenTicket"
                value={yesNoToOption(url.golden)}
                onChange={(event) => setFilters({ golden: optionToYesNo(event.target.value) })}
                options={[
                  { value: '', label: 'Any' },
                  { value: 'yes', label: 'Has golden ticket' },
                  { value: 'no', label: 'No golden ticket' },
                ]}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
                Default scope
              </span>
              <Select
                id="admin.users.scope"
                value={url.scope}
                onChange={(event) => setFilters({ scope: event.target.value })}
                options={scopeOptions}
              />
            </label>
          </div>
        }
      />

      <DataGrid<UserRow>
        data={rows}
        columns={columns}
        loading={loading && !viewData}
        rowId={(row) => row.id}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
